import React, { useState } from 'react'
import { LuCheck, LuTriangleAlert } from 'react-icons/lu'

import Select from '@/components/select/Select'
import { Button } from '@/components/ui/button'
import {
  BulkUpdatePayload,
  ReferralsImportItem,
  ReferralsImportItemPartial,
} from '@/redux/slices/referralsImportBase'

interface Props {
  importItem: ReferralsImportItem
  updateImportItem: (item: ReferralsImportItemPartial) => void
  bulkUpdateItems: (payload: BulkUpdatePayload) => void
  bulkUpdateSelectedOnly: boolean
}

interface AdviserOption {
  label: string
  value: number
}

const FromCompanyAdviserCell: React.FC<Props> = ({
  importItem,
  updateImportItem,
  bulkUpdateItems,
  bulkUpdateSelectedOnly,
}) => {
  const [isChanging, setIsChanging] = useState(false)
  const fromCompanyUsers = importItem.fromCompany?.users || []

  const adviserOptions: AdviserOption[] = fromCompanyUsers.map((user) => ({
    label: `${user.firstName} ${user.lastName}`,
    value: user.id,
  }))
  if (importItem.fromAdviserEmail) {
    adviserOptions.unshift({
      label: importItem.fromAdviserEmail,
      value: -1,
    })
  }

  function handleChange(selectedOption: AdviserOption | null) {
    const fromCompanyUser = selectedOption
      ? fromCompanyUsers.find((user) => user.id === selectedOption.value) ||
        null
      : null

    updateImportItem({
      refUUID: importItem.refUUID,
      fromCompanyUser: fromCompanyUser,
      fromCompanyUserDefaultUsed: false,
    })

    setIsChanging(false)
  }

  function handleBulkApply() {
    bulkUpdateItems({
      filter: {
        selected: bulkUpdateSelectedOnly,
        fromCompany: importItem.fromCompany?.id,
      },
      data: {
        fromCompanyUser: importItem.fromCompanyUser,
        fromCompanyUserDefaultUsed: false,
      },
    })
  }

  const bulkUpdateScope =
    importItem.bulkFromCompanyCount > 1
      ? !bulkUpdateSelectedOnly
        ? 'all'
        : importItem.selected
          ? 'selected'
          : 'none'
      : 'none'

  if (!importItem.fromCompany) {
    return null
  }

  if (isChanging) {
    return (
      <>
        <Select
          options={adviserOptions}
          onChange={handleChange}
          onBlur={() => setIsChanging(false)}
          value={adviserOptions.find(
            (adviserOption) =>
              adviserOption.value == importItem.fromCompanyUser?.id,
          )}
          className="mt-1"
        />

        <span className="mt-1 block text-right">
          <Button
            size="icon-xs"
            variant="outline-primary"
            onClick={() => setIsChanging(false)}
          >
            <LuCheck />
          </Button>
        </span>
      </>
    )
  }

  if (importItem.fromCompanyUser) {
    return (
      <span className="text-dim text-xs">
        <span
          onClick={() => setIsChanging(true)}
          className="cursor-pointer font-semibold"
        >
          {importItem.fromCompanyUser.firstName}{' '}
          {importItem.fromCompanyUser.lastName}
        </span>
        , <span className="break-all">{importItem.fromCompanyUser.email}</span>
        {importItem.fromCompanyUserDefaultUsed && (
          <>
            <span className="text-warning"> (default)</span>
          </>
        )}
        {importItem.fromCompanyUserDefaultUsed &&
          importItem.sourceFromAdviserEmail != '' && (
            <>
              ,<br />
              <span
                className="text-warning break-all"
                style={{ maxWidth: '300px' }}
              >
                <LuTriangleAlert className="mr-1 inline-block" />
                adviser with email address “{importItem.sourceFromAdviserEmail}”
                not found, select from available advisers or leave default
              </span>
            </>
          )}
        <span className="show-on-row-hover">
          ,{' '}
          <Button variant="plain" onClick={() => setIsChanging(true)}>
            change
          </Button>
          {bulkUpdateScope == 'all' && (
            <>
              {' or '}
              <Button
                variant="plain"
                onClick={handleBulkApply}
                title="Apply this adviser for all referrals for this company"
              >
                apply&nbsp;to&nbsp;all&nbsp;for&nbsp;this&nbsp;company&nbsp;(
                {importItem.bulkFromCompanyCount})
              </Button>
            </>
          )}
          {bulkUpdateScope == 'selected' && (
            <>
              {' or '}
              <Button
                variant="plain"
                onClick={handleBulkApply}
                title="Apply this adviser for selected referrals for this company"
              >
                apply&nbsp;to&nbsp;selected&nbsp;for&nbsp;this&nbsp;company
              </Button>
            </>
          )}
        </span>
      </span>
    )
  }

  if (adviserOptions.length > 0) {
    return (
      <span className="text-dim text-xs">
        {importItem.isFromInvite
          ? 'New adviser selected'
          : 'No specific adviser selected'}
        <span className="show-on-row-hover">
          ,{' '}
          <Button variant="plain" onClick={() => setIsChanging(true)}>
            choose
          </Button>
        </span>
      </span>
    )
  }

  return (
    <>
      <div className="text-dim mt-1 text-xs">
        <LuTriangleAlert className="mr-1 inline-block" />
        Not possible to select adviser. No advisers available for this company.
      </div>
    </>
  )
}

export default FromCompanyAdviserCell
